<template>
  <div>
    <title-row
      title="Eszköznapló"
      :breadcrumb-items="[
        { text: 'Eszközök', to: { name: 'AssetList' } },
        { text: 'Napló', disabled: true }
      ]">
      <template #buttons>
        <title-row-button
          :disabled="options.page != 1"
          :title="options.page == 1 ? '' : 'A frissítés opció csak az első oldalon működik'"
          @click="togglePolling">
          <v-icon v-if="polling">mdi-stop</v-icon>
          <v-icon v-else>mdi-play</v-icon>
          Frissítés
        </title-row-button>
      </template>
    </title-row>

    <v-expansion-panels
      class="mb-4 rounded-xl overflow-hidden"
      :mandatory="!!Object.values(search).find((e) => e !== '')"
      flat>
      <v-expansion-panel>
        <v-expansion-panel-header>
          <span class="font-weight-medium">
            <v-icon left>mdi-magnify</v-icon>
            Keresés
          </span>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-row class="mt-2" align="end">
            <v-col :cols="12" :md="3" :lg="2">
              <v-autocomplete
                label="Szekrény"
                :items="cabinets"
                item-text="name"
                item-value="id"
                dense
                clearable
                hide-details
                v-model="search.cabinet_ids"
                placeholder="Összes"
                @change="periodChangeEventHandler"
                multiple />
            </v-col>
            <v-col :cols="12" :md="3" :lg="2">
              <v-text-field
                label="Név / Kártyaszám"
                dense
                clearable
                hide-details
                v-model="search.keyphrase"
                @input="periodChangeEventHandler" />
            </v-col>
            <v-col :cols="12" :md="2" :lg="2">
              <v-autocomplete
                label="Használatban"
                :items="[
                  { text: 'Igen', value: true },
                  { text: 'Nem', value: false },
                ]"
                dense
                v-model="search.taken"
                :value-comparator="$config.comparators.isEqual"
                hide-details
                clearable
                @input="searchEventHandler" />
            </v-col>
            <v-col :cols="12" :md="3" :lg="2">
              <v-select
                label="Időkorlát"
                :items="timePeriods"
                item-text="label"
                item-value="value"
                dense
                hide-details
                v-model="search.period"
                @change="periodChangeEventHandler" />
            </v-col>
            <template v-if="search.period === 'custom'">
              <v-col :cols="12" :md="3" :lg="2">
                <v-datetime-picker
                  label="Időkorlát kezdete"
                  v-model="search.from"
                  clearable
                  dense
                  :date-picker-props="{ allowedDates: allowedStartDates }"
                  :time-picker="false"
                  @change="periodChangeEventHandler"
                  :error-messages="search.from ? '' : 'Kötelező!'" />
              </v-col>
              <v-col :cols="12" :md="2" :lg="2">
                <v-datetime-picker
                  label="Időkorlát vége"
                  v-model="search.to"
                  clearable
                  dense
                  :date-picker-props="{ allowedDates: allowedEndDates }"
                  :time-picker="false"
                  :disabled="!search.from"
                  @change="periodChangeEventHandler"
                  :error-messages="search.from && !search.to ? 'Kötelező!' : ''" />
              </v-col>
            </template>
            <v-col
              :cols="12"
              :sm="2"
              :md="2">
              <v-btn
                color="primary"
                class="rounded-xl"
                depressed
                @click="fetchItems">
                <v-icon left>mdi-magnify</v-icon>
                Szűrés
              </v-btn>
            </v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-card class="rounded-xl" flat>
      <v-data-table
        :headers="dataTable.headers"
        :items="dataTable.items"
        :server-items-length="dataTable.itemsLength"
        :options.sync="dataTable.options"
        :footer-props="$config.dataTableFooterProps"
        calculate-widths>
        <template #[`item.created`]="{ item }">
          <span :title="item.created | moment($config.momentFormats.explicit)">{{
            item.created | moment('lll')
          }}</span>
        </template>
        <template #[`item.assets`]="{ item }">
          <div class="d-flex flex-column gap-y-1">
            <div v-for="asset in item.assets" :key="`asset-${asset.id}`">
              <router-link :to="{ name: 'AssetEditor', params: { id: asset.id } }" class="d-block">
                <div class="font-weight-bold">{{ asset.name }}</div>
              </router-link>
            </div>
          </div>
        </template>
        <template #[`item.asset_name`]="{ item }">
          <span class="font-weight-bold">{{ item.asset_name }}</span>
        </template>
        <template #[`item.owner_name`]="{ item }">
          <div class="font-weight-bold">{{ item.owner_name }}</div>
          <div class="text-caption">{{ item.card_number }}</div>
        </template>
        <template #[`item.event`]="{ item }">
          <v-chip v-if="item.event === 'return'" color="success" dark>Visszavétel</v-chip>
          <v-chip v-else-if="item.event === 'take' && item.unauthorized == 0" color="warning" dark>Kiadás</v-chip>
          <v-chip v-else-if="item.event === 'take' && item.unauthorized == 1" color="error" dark>Kiadás (illetéktelen)</v-chip>
        </template>
        <template #[`item.actions`]="{ item }">
          <v-btn
            v-if="item.element_id && hasRight('CARD_READ')"
            :to="{ name: 'CardEditor', params: { id: item.element_id } }"
            fab
            small
            color="primary"
            dark
            text
            class="rounded-sm">
            <v-icon>mdi-card-account-details</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>
<script>
import { dataTablePage } from '@/mixins';

export default {
  mixins: [dataTablePage],

  data() {
    return {
      polling: false,
      buildingType: 'P',
      waiting: false,
      routeName: 'AssetsLog',
      fetchItems: this.fetchAssetsLog,
      dataTable: {
        options: {
          sortBy: ['logid'],
          sortDesc: [true],
        },
        headers: [
          { text: 'Idő', value: 'created' },
          { text: 'Eszköz', value: 'assets', class: 'py-1' },
          { text: 'Birtokos', value: 'owner_name' },
          { text: 'Esemény', value: 'event', sortable: false, width: 120 },
          { text: 'Tároló', value: 'cabinet_name', sortable: false, width: 200 },
          { text: 'Megjegyzés', value: 'remark', sortable: false, width: 200 },
          { value: 'actions', sortable: false },
        ],
      },
      timePeriods: [
        {
          label: 'Utolsó 24 óra',
          value: '1 days',
        },
        {
          label: 'Utolsó hét',
          value: '1 weeks',
        },
        {
          label: 'Utolsó hónap',
          value: '1 months',
        },
        {
          label: 'Egyéni',
          value: 'custom',
        },
      ],
      search: {
        cabinet_ids: [],
        keyphrase: '',
        taken: null,
        log_type: 'enter',
        building_id: [],
        building_type: 'P',
        period: '1 days',
        controller_id: [],
        from: '',
        to: '',
      },
      logs: [],
      pollingInterval: null,
      pollingTimeout: null,
      pollingPausedByPagination: false,
      firstFetch: true,
    };
  },

  created() {
    if (this.options.page == 1) {
      this.polling = true;
    }
  },

  beforeDestroy() {
    this.polling = false;
    clearTimeout(this.pollingTimeout);
  },

  watch: {
    'dataTable.options': {
      handler(val) {
        if (val.page > 1) {
          if (this.polling) {
            clearTimeout(this.pollingTimeout);
            this.polling = false;
            this.pollingPausedByPagination = true;
          }
        } else if (this.pollingPausedByPagination) {
          this.fetchParkingLog(true);
          this.polling = true;
          this.pollingPausedByPagination = false;
        }
      },
      deep: true,
    },
  },

  methods: {
    parseSearchQueries() {
      return {
        keyphrase: this.$route.query.keyphrase,
        log_type: this.$route.query.log_type,
        period: this.$route.query.period,
        taken: this.$route.taken,
        controller_id: this.$route.query.controller_id
          ? this.toArray(this.$route.query.controller_id)
          : [],
        building_id: this.$route.query.building_id
          ? this.toArray(this.$route.query.building_id)
          : [],
        from: this.$route.query.from,
        to: this.$route.query.to,
      };
    },

    toArray(param) {
      return Array.isArray(param) ? param : [param];
    },

    togglePolling() {
      this.polling = !this.polling;
      if (this.polling) {
        this.fetchParkingLog(true);
      } else {
        clearTimeout(this.pollingTimeout);
      }
    },

    allowedStartDates(val) {
      const now = new Date();
      const startDate = new Date(val);
      if (this.search.to?.length) {
        let intervalStart = new Date(this.search.to);
        intervalStart.setMonth(intervalStart.getMonth() - 2);
        return (
          startDate.getTime() <= now.getTime() && startDate.getTime() >= intervalStart.getTime()
        );
      }
      return startDate.getTime() <= now.getTime();
    },

    allowedEndDates(val) {
      const now = new Date();
      const startDate = new Date(this.search.from);
      const endDate = new Date(val);
      let intervalEnd = new Date(this.search.from);
      intervalEnd.setMonth(intervalEnd.getMonth() + 2);
      intervalEnd = Math.min(intervalEnd.getTime(), now.getTime());
      return endDate.getTime() >= startDate.getTime() && endDate.getTime() <= intervalEnd;
    },

    periodChangeEventHandler(val) {
      if (
        this.search.period === 'custom' &&
        (!this.search.from?.length || !this.search.to?.length)
      ) {
        return;
      }
      this.searchEventHandler();
    },

    async fetchAssetsLog(keepPolling = false) {
      if (this.firstFetch) {
        keepPolling = this.polling;
        this.firstFetch = false;
      }

      this.loading = true;
      try {
        console.log(this.postData);
        const response = await this.$http.post('assets/log', this.postData);
        console.log(response);
        if (this.polling && keepPolling) {
          this.pollingTimeout = setTimeout(() => {
            this.fetchAssetsLog(true);
          }, 10000);
        }

        const logs = [];
        for (const log of response.logs) {
          log.assets = JSON.parse(log.assets);
          logs.push(log);
        }
        this.dataTable.items = logs;
        this.dataTable.itemsLength = response.logs_count;
        this.dataTable.options.page = this.$route.query.page * 1 || 1;
      } catch (e) {
        this.polling = false;
        console.error(e);
      } finally {
        this.loading = false;
      }
    },
  },

  computed: {
    searchQueries() {
      return {
        cabinet_ids: this.search.cabinet_ids.map((e) => Number(e)) || [],
        keyphrase: this.search.keyphrase || '',
        period: this.search.period || '',
        log_type: this.search.log_type || '',
        building_id: this.search.building_id.map((e) => Number(e)) || [],
        controller_id: this.search.controller_id.map((e) => Number(e)) || [],
        building_type: this.search.building_type || '',
        from: this.search.from || '',
        to: this.search.to || '',
        taken: this.search.taken ?? null,
      };
    },
  },

  asyncComputed: {
    buildings: {
      async get() {
        const response = await this.$http.post('buildings/list', {
          type: 'P', // Parkolók
        });
        return response.buildings;
      },
      default: [],
    },
    cabinets: {
      async get() {
        const response = await this.$http.post('asset-cabinets/list');
        return response.asset_cabinets;
      },
      default: [],
    },
  },
};
</script>

<style scoped>
::v-deep tbody tr {
  vertical-align: top !important;
}
</style>